import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components";
import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import { withTotalInTitle } from "../with-total-in-title";

const getDistributionChannelData = (
  items: ReportingItem[] | undefined,
  distributionChannelDefinition?: { id: string; label: string }[]
) => {
  const channelCounts = new Map<string, { label: string; count: number }>();

  for (const item of items ?? []) {
    const channelIds = item.distributionChannel ?? [];
    if (!channelIds.length) continue;

    for (const channelId of channelIds) {
      const existing = channelCounts.get(channelId) || {
        label:
          distributionChannelDefinition?.find((c) => c.id === channelId)
            ?.label || channelId,
        count: 0,
      };
      channelCounts.set(channelId, {
        ...existing,
        count: existing.count + 1,
      });
    }
  }

  const entries = Array.from(channelCounts.values()).map((item, index) => ({
    label: item.label,
    count: item.count,
    color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
  }));

  return { entries };
};

/** Adds the distribution-channels bar chart and donut chart (#2222). */
export function addDistributionChannelCards(
  view: View<TestAppState, DragAndDropLayout>
) {
  // Distribution Channels
  const distributionChannelsBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "distributionChannelsBarChartComponent",
      labels: ["barChart", "chart", "distributionChannel"],
      description: "Shows systems grouped by distribution channel.",
      title: "Systems per Distribution Channels (Bar Chart)",
    },
  });

  distributionChannelsBarChartComponent.mapState(
    ({ currentReport, settings }) => {
      const items = (currentReport?.project?.reportingItems ?? []).filter(
        (item) => !item?.isChannelHeader
      );
      const { entries } = getDistributionChannelData(
        items,
        settings?.configuration?.distributionChannelDefinition
      );

      return {
        title: "Systems per Distribution Channels (Bar Chart)",
        categories: entries.map((entry) => entry.label),
        data: entries.map((entry) => entry.count),
        chartType: 1 as const,
        xLabel: "Systems",
        yLabel: "Distribution Channel",
        colors: entries.map((entry) => entry.color),
        fixedHeight: "320px",
      };
    }
  );

  const distributionChannelsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "distributionChannelsDonutChartComponent",
      labels: ["donutChart", "chart", "distributionChannel"],
      description: "Shows systems grouped by distribution channel.",
      title: "Systems per Distribution Channels",
    },
  });

  distributionChannelsDonutChartComponent.mapState(
    ({ currentReport, settings }) => {
      const items = (currentReport?.project?.reportingItems ?? []).filter(
        (item) => !item?.isChannelHeader
      );
      const { entries } = getDistributionChannelData(
        items,
        settings?.configuration?.distributionChannelDefinition
      );

      return {
        title: withTotalInTitle(
          "Systems per Distribution Channels",
          items.length
        ),
        icon: "DonutLarge",
        ...DONUT_CARD_LAYOUT,
        data: entries.map((entry) => ({
          label: entry.label,
          value: entry.count,
          color: entry.color,
        })),
      };
    }
  );
}
